import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export const THEME_STORAGE_KEY = "flots_theme";

/** What the visitor chose. `system` follows the OS `prefers-color-scheme`. */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/**
 * The default. The app (monorepo ThemeContext) ships light-first because Firefox reports
 * `prefers-color-scheme` from its own setting; on the marketing site the visitor's device
 * preference is the expected behaviour, and the footer toggle is the way out.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system";

/** Surface colour for the browser chrome (`theme-color`), per resolved theme. */
export const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: "#ffffff",
  dark: "#0b0f17",
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCE;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemePreference(stored)) return stored;
  } catch {
    // Storage blocked (private mode, cookie settings): fall back to the default.
  }
  return DEFAULT_THEME_PREFERENCE;
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") return systemPrefersDark() ? "dark" : "light";
  return preference;
}

/** Puts the resolved theme on <html> (`.dark` + `theme-color`), as the pre-paint script does. */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLOR[resolved];
}

type ThemeState = {
  preference: ThemePreference;
};

// The server cannot read localStorage, so the first render uses the default and `hydrate`
// catches up; the pre-paint script in app/layout.tsx has already set the right class.
const initialState: ThemeState = { preference: DEFAULT_THEME_PREFERENCE };

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    hydrate: (state) => {
      state.preference = readStoredPreference();
    },
    setPreference: (state, action: PayloadAction<ThemePreference>) => {
      state.preference = action.payload;
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, action.payload);
      } catch {
        // Nothing to persist to: the choice holds for this visit only.
      }
    },
  },
});

export const { hydrate, setPreference } = themeSlice.actions;
export default themeSlice.reducer;
export type { ThemeState };
